import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Progress } from '../ui/progress';
import { Badge } from '../ui/badge';
import { Brain, Target, TrendingUp, AlertCircle, CheckCircle, Sparkles, ArrowRight } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const DiagnosticoIA = ({ colaboradorId, onDiagnosticoConcluido }) => {
  const [loading, setLoading] = useState(false);
  const [diagnostico, setDiagnostico] = useState(null);
  const [competenciasSelecionadas, setCompetenciasSelecionadas] = useState([]);
  const [mostrarProximoPasso, setMostrarProximoPasso] = useState(false);

  // Carregar diagnóstico existente ao montar componente
  useEffect(() => {
    if (colaboradorId) {
      carregarDiagnosticoExistente();
    }
  }, [colaboradorId]);

  const carregarDiagnosticoExistente = async () => {
    try {
      const response = await axios.get(
        `${API}/api/pdi-completo/diagnostico-ia/${colaboradorId}`,
        {
          headers: { 'Authorization': `Bearer ${localStorage.getItem('token')}` }
        }
      );
      setDiagnostico(response.data);
      toast.info('Diagnóstico anterior carregado');
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Erro ao carregar diagnóstico:', error);
      }
      // 404 é normal quando não há diagnóstico ainda
    }
  };

  const gerarDiagnostico = async () => {
    try {
      setLoading(true);
      
      const response = await axios.post(`${API}/api/pdi-completo/diagnostico-ia`, null, {
        params: {
          colaborador_id: colaboradorId,
          incluir_avaliacao: true,
          incluir_fit_cultural: true,
          incluir_nine_box: true
        }
      });
      
      setDiagnostico(response.data);
      
      // Mensagem personalizada baseada nas fontes
      const fontes_count = response.data.fontes_utilizadas?.length || 0;
      const fontes_faltando = response.data.fontes_indisponiveis?.length || 0;
      
      if (fontes_faltando === 0) {
        toast.success(`Diagnóstico gerado com ${fontes_count} fontes completas! 🎉`);
      } else {
        toast.warning(
          `Diagnóstico gerado com ${fontes_count} fontes. ${fontes_faltando} módulos sem dados.`,
          { duration: 5000 }
        );
      }
      
    } catch (error) {
      console.error('Erro ao gerar diagnóstico:', error);
      
      if (error.response?.status === 404) {
        toast.error('Colaborador não possui dados suficientes para diagnóstico. É necessário ter pelo menos um módulo preenchido (Avaliação, Feedback, Autoavaliação, etc).');
      } else {
        toast.error('Erro ao gerar diagnóstico. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const toggleCompetencia = (competencia) => {
    console.log('toggleCompetencia chamada com:', competencia);
    
    setCompetenciasSelecionadas(prev => {
      console.log('Estado anterior:', prev);
      
      const competenciaId = competencia.competencia_id || competencia.competencia_nome;
      const existe = prev.find(c => 
        (c.competencia_id && c.competencia_id === competenciaId) || 
        (c.competencia_nome === competencia.competencia_nome)
      );
      
      if (existe) {
        console.log('Removendo competência');
        return prev.filter(c => 
          (c.competencia_id && c.competencia_id !== competenciaId) && 
          (c.competencia_nome !== competencia.competencia_nome)
        );
      } else {
        console.log('Adicionando competência');
        return [...prev, competencia];
      }
    });
  };

  const confirmarDiagnostico = () => {
    if (competenciasSelecionadas.length === 0) {
      toast.error('Selecione pelo menos uma competência');
      return;
    }

    if (onDiagnosticoConcluido) {
      onDiagnosticoConcluido(competenciasSelecionadas);
    }
    
    // Mostrar próximo passo
    setMostrarProximoPasso(true);
    toast.success(`${competenciasSelecionadas.length} competências selecionadas!`);
  };

  const getPrioridadeColor = (prioridade) => {
    const colors = {
      'critica': 'bg-red-100 text-red-800',
      'alta': 'bg-orange-100 text-orange-800',
      'media': 'bg-yellow-100 text-yellow-800',
      'baixa': 'bg-green-100 text-green-800'
    };
    return colors[prioridade] || colors['media'];
  };

  return (
    <div className="space-y-6">
      <Card className="border-2 border-purple-200 bg-gradient-to-br from-purple-50 to-blue-50">
        <CardHeader>
          <CardTitle className="flex items-center space-x-3">
            <div className="p-2 bg-purple-600 rounded-lg">
              <Brain className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold">Diagnóstico Inteligente de Necessidades</h3>
              <p className="text-sm text-gray-600 font-normal">
                Análise automatizada com base em suas avaliações
              </p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {!diagnostico ? (
            <div className="text-center py-8">
              <Sparkles className="h-16 w-16 text-purple-600 mx-auto mb-4" />
              <p className="text-gray-600 mb-4">
                Clique no botão abaixo para gerar um diagnóstico automático das suas necessidades de desenvolvimento
              </p>
              <Button 
                onClick={gerarDiagnostico} 
                disabled={loading}
                className="bg-purple-600 hover:bg-purple-700"
              >
                {loading ? 'Analisando...' : 'Gerar Diagnóstico com IA'}
              </Button>
            </div>
          ) : (
            <div className="space-y-6">
              {/* Fontes Analisadas */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center">
                  <CheckCircle className="h-5 w-5 text-green-600 mr-2" />
                  Fontes Analisadas
                </h4>
                <div className="flex flex-wrap gap-2">
                  {diagnostico.fontes.map((fonte, idx) => (
                    <Badge key={idx} className="bg-green-100 text-green-800">
                      {fonte}
                    </Badge>
                  ))}
                </div>
                
                {/* Aviso sobre fontes indisponíveis */}
                {diagnostico.fontes_indisponiveis && diagnostico.fontes_indisponiveis.length > 0 && (
                  <div className="mt-4 p-4 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <div className="flex items-start space-x-3">
                      <AlertCircle className="h-5 w-5 text-yellow-600 mt-0.5 flex-shrink-0" />
                      <div className="text-sm">
                        <p className="font-semibold text-yellow-900 mb-2">
                          Módulos sem dados ({diagnostico.fontes_indisponiveis.length}):
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {diagnostico.fontes_indisponiveis.map((fonte, idx) => (
                            <Badge key={idx} variant="outline" className="border-yellow-400 text-yellow-700">
                              {fonte}
                            </Badge>
                          ))}
                        </div>
                        <p className="text-yellow-800 mt-3">
                          💡 A análise foi realizada com base apenas nos {diagnostico.total_fontes || diagnostico.fontes?.length || 0} módulos disponíveis.
                          Para um diagnóstico mais completo, preencha os módulos acima.
                        </p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Informação sobre modelo IA usado */}
                {diagnostico.modelo_ia && (
                  <div className="mt-4 p-3 bg-purple-50 border border-purple-200 rounded-lg">
                    <div className="flex items-center space-x-2 text-sm text-purple-800">
                      <Sparkles className="h-4 w-4" />
                      <span className="font-semibold">Modelo IA:</span>
                      <span>{diagnostico.modelo_ia}</span>
                    </div>
                  </div>
                )}
              </div>

              {/* Competências Sugeridas */}
              <div>
                <h4 className="font-semibold mb-3 flex items-center">
                  <Target className="h-5 w-5 text-purple-600 mr-2" />
                  Competências Recomendadas ({diagnostico.competencias_sugeridas?.length || 0})
                </h4>
                
                {diagnostico.competencias_sugeridas?.length === 0 ? (
                  <div className="text-center py-6 bg-gray-50 rounded-lg">
                    <p className="text-gray-600">Nenhuma competência crítica identificada</p>
                    <p className="text-sm text-gray-500 mt-2">Seu desempenho está equilibrado!</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {diagnostico.competencias_sugeridas?.map((competencia, idx) => {
                      const competenciaId = competencia.competencia_id || competencia.competencia_nome;
                      const selecionada = competenciasSelecionadas.find(
                        c => (c.competencia_id && c.competencia_id === competenciaId) || 
                             (c.competencia_nome === competencia.competencia_nome)
                      );
                      
                      return (
                        <Card 
                          key={competenciaId || idx}
                          className={`cursor-pointer transition-all ${
                            selecionada 
                              ? 'border-2 border-purple-500 bg-purple-50' 
                              : 'border hover:border-purple-300'
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleCompetencia(competencia);
                          }}
                        >
                          <CardContent className="p-4">
                            <div className="flex items-start justify-between">
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h5 className="font-semibold">{competencia.competencia_nome}</h5>
                                  <Badge className={getPrioridadeColor(competencia.prioridade)}>
                                    {competencia.prioridade?.toUpperCase()}
                                  </Badge>
                                  {competencia.sugerida_por_ia && (
                                    <Badge className="bg-purple-100 text-purple-800">
                                      <Sparkles className="h-3 w-3 mr-1" />
                                      IA
                                    </Badge>
                                  )}
                                </div>
                                
                                <p className="text-sm text-gray-600 mb-3">
                                  {competencia.justificativa}
                                </p>
                                
                                <div className="flex items-center space-x-4 text-sm">
                                  <div>
                                    <span className="text-gray-500">Nível Atual:</span>
                                    <span className="ml-2 font-semibold text-orange-600">
                                      {competencia.nivel_atual?.toFixed(1)}
                                    </span>
                                  </div>
                                  <ArrowRight className="h-4 w-4 text-gray-400" />
                                  <div>
                                    <span className="text-gray-500">Nível Sugerido:</span>
                                    <span className="ml-2 font-semibold text-green-600">
                                      {competencia.nivel_sugerido?.toFixed(1)}
                                    </span>
                                  </div>
                                  <div>
                                    <span className="text-gray-500">Gap:</span>
                                    <span className="ml-2 font-semibold text-blue-600">
                                      {(competencia.nivel_sugerido - competencia.nivel_atual).toFixed(1)}
                                    </span>
                                  </div>
                                </div>
                              </div>
                              
                              <div className="ml-4">
                                {selecionada ? (
                                  <CheckCircle className="h-6 w-6 text-purple-600" />
                                ) : (
                                  <div className="h-6 w-6 border-2 border-gray-300 rounded-full" />
                                )}
                              </div>
                            </div>
                          </CardContent>
                        </Card>
                      );
                    })}
                  </div>
                )}
              </div>

              {/* Ações */}
              <div className="flex items-center justify-between pt-4 border-t">
                <div className="text-sm text-gray-600">
                  {competenciasSelecionadas.length} competência(s) selecionada(s)
                </div>
                <div className="flex space-x-3">
                  <Button 
                    variant="outline" 
                    onClick={gerarDiagnostico}
                    disabled={loading}
                  >
                    Gerar Novo
                  </Button>
                  <Button 
                    onClick={confirmarDiagnostico}
                    disabled={competenciasSelecionadas.length === 0}
                    className="bg-purple-600 hover:bg-purple-700"
                  >
                    Confirmar Seleção
                  </Button>
                </div>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Próximo Passo - Navegação */}
      {mostrarProximoPasso && (
        <Card className="border-2 border-green-500 bg-green-50">
          <CardHeader className="bg-gradient-to-r from-green-500 to-blue-500 text-white">
            <CardTitle className="flex items-center space-x-3">
              <CheckCircle className="h-8 w-8" />
              <div>
                <h3 className="text-2xl font-bold">Diagnóstico Concluído! 🎉</h3>
                <p className="text-sm text-white/90 font-normal mt-1">
                  {competenciasSelecionadas.length} competências selecionadas para desenvolvimento
                </p>
              </div>
            </CardTitle>
          </CardHeader>
          <CardContent className="p-6 space-y-6">
            <div className="bg-white rounded-lg p-4">
              <h4 className="font-semibold text-gray-900 mb-3">📚 Próximos Passos:</h4>
              <div className="space-y-3">
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-600 font-bold">
                    2
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Biblioteca de Recursos</p>
                    <p className="text-sm text-gray-600">
                      Navegue por cursos, livros e treinamentos recomendados para desenvolver as competências selecionadas
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-purple-100 flex items-center justify-center text-purple-600 font-bold">
                    3
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Kanban PDI</p>
                    <p className="text-sm text-gray-600">
                      Crie e organize suas ações de desenvolvimento no quadro Kanban
                    </p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 font-bold">
                    4
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">Timeline & Acompanhamento</p>
                    <p className="text-sm text-gray-600">
                      Monitore seu progresso e atualize status das ações
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex justify-between items-center">
              <Button
                variant="outline"
                onClick={() => setMostrarProximoPasso(false)}
              >
                Voltar ao Diagnóstico
              </Button>
              <Button
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  toast.info('Navegue pelos módulos acima para continuar');
                }}
              >
                Entendi, vou continuar!
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default DiagnosticoIA;
